import { getAllContactsThunk } from '@redux/features/contact.slice';
import { AnimatedListSkeleton, AnimatedTable, AnimatedTableBody, AnimatedTableRow } from 'components/animations';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import BreadCrumb from 'Common/BreadCrumb';
import Pagination from 'Common/Pagination';
import React, { FC, useEffect, useState } from 'react';

const ContactSubmissions: FC = () => {
    const [page, setPage] = useState<number>(1);

    const dispatch = useAppDispatch();
    const { contacts, getAllContactsLoading, pagination } = useAppSelector(
        (state: RootState) => state.contactState
    );

    // -------------------------------------------------------------
    useEffect(() => {
        dispatch(
            getAllContactsThunk({
                pageNumber: page - 1,
                pageSize: 20,
            })
        );
    }, [page, dispatch]);
    // -------------------------------------------------------------

    const changePage = (newPage: number) => {
        setPage(newPage);
    };

    // Helper function to format date
    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return dateString;
        }
    };

    // Helper function to truncate message
    const truncateMessage = (message: string, maxLength: number = 50) => {
        if (!message) return '-';
        if (message.length <= maxLength) return message;
        return message.substring(0, maxLength) + '...';
    };

    // -------------------------------------------------------------

    return (
        <>
            <BreadCrumb pageTitle={`Contact Submissions`} />
            <section className="py-5">
                <div className=" p-3 flex flex-col gap-2 bg-[#fff]">
                    <div className="flex w-full items-center justify-between">
                        <h3 className="font-extrabold text-[#bd902e]">Contact Submissions List</h3>
                    </div>
                    {getAllContactsLoading ? (
                        <AnimatedListSkeleton rows={8} />
                    ) : (
                        <>
                            <div>
                                <div className="overflow-x-auto">
                                    <AnimatedTable className="table-custom w-full border-separate border-spacing-y-1 whitespace-nowrap">
                                        <thead className="ltr:text-left rtl:text-right">
                                            <tr
                                                className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                    after:left-0 after:top-0 after:border-l-2
                    after:border-transparent [&.active]:after:border-custom-500"
                                            >
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">ID</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">First Name</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Last Name</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Company</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Email</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Phone</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Message</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Submitted Date</th>
                                            </tr>
                                        </thead>
                                        <AnimatedTableBody loading={getAllContactsLoading} itemCount={contacts?.length ?? 0} animationKey={`contact-${page}`}>
                                            {contacts && contacts.length > 0 ? (
                                                contacts.map((contact: any) => (
                                                    <AnimatedTableRow
                                                        rowKey={contact?.id}
                                                        detailRecord={contact}
                                                        className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                        after:left-0 after:top-0 after:border-l-2 hover:bg-[#bd902e10]
                        after:border-transparent [&.active]:after:border-custom-500"
                                                    >
                                                        <td className="px-3.5 py-2.5">{contact?.id || `-`}</td>
                                                        <td className="px-3.5 py-2.5">{contact?.firstName || `-`}</td>
                                                        <td className="px-3.5 py-2.5">{contact?.lastName || `-`}</td>
                                                        <td className="px-3.5 py-2.5">{contact?.company || `-`}</td>
                                                        <td className="px-3.5 py-2.5">{contact?.email || `-`}</td>
                                                        <td className="px-3.5 py-2.5">{contact?.phone || `-`}</td>
                                                        <td className="px-3.5 py-2.5" title={contact?.message || ''}>
                                                            {truncateMessage(contact?.message)}
                                                        </td>
                                                        <td className="px-3.5 py-2.5">{formatDate(contact?.createdAt)}</td>
                                                    </AnimatedTableRow>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={8} className="px-3.5 py-2.5 text-center text-gray-500">
                                                        No contact submissions found
                                                    </td>
                                                </tr>
                                            )}
                                        </AnimatedTableBody>
                                    </AnimatedTable>
                                </div>
                                <Pagination
                                    perPageData={pagination?.pageSize || 20}
                                    total={pagination?.totalElements || 0}
                                    currentPage={page}
                                    changePage={changePage}
                                    length={contacts?.length || 0}
                                />
                            </div>
                        </>
                    )}
                </div>
            </section>
        </>
    );
};

export default ContactSubmissions;

